import {
  CircularProgress,
  InputAdornment,
  ListItem,
  ListItemText,
  Paper,
  TextField
} from '@material-ui/core'
import MagnifyIcon from 'mdi-react/MagnifyIcon'
import { FC, useEffect, useRef, useState } from 'react'

// PRH:n avoin YTJ-rajapinta (v3), sama kuin Fiksumyynnin putkessa — ei API-avainta, CORS sallittu
const YTJ_API_URL = 'https://avoindata.prh.fi/opendata-ytj-api/v3/companies'
const ACCEPTED_COMPANY_FORMS = ['Asunto-osakeyhtiö', 'Keskinäinen kiinteistöosakeyhtiö']
const MIN_QUERY_LENGTH = 4
const DEBOUNCE_MS = 400
const MAX_SUGGESTIONS = 8

export interface YtjCompanySuggestion {
  name: string
  businessId: string
}

const parseYtjResponse = (data: any): YtjCompanySuggestion[] => {
  const companies: any[] = data?.companies ?? []
  return companies
    .filter(
      company =>
        !company.endDate &&
        (company.companyForms ?? []).some(
          (form: any) =>
            !form.endDate &&
            (form.descriptions ?? []).some((d: any) =>
              ACCEPTED_COMPANY_FORMS.includes(d.description)
            )
        )
    )
    .map(company => {
      const currentName =
        (company.names ?? []).find((n: any) => n.type === '1' && !n.endDate) ??
        (company.names ?? [])[0]
      return {
        name: currentName?.name ?? '',
        businessId: company.businessId?.value ?? ''
      }
    })
    .filter(s => s.name && s.businessId)
    .slice(0, MAX_SUGGESTIONS)
}

interface HousingCompanySearchFieldProps {
  label: string
  value: string
  error?: boolean
  helperText?: string
  manualEntryLabel: string
  onChange: (name: string) => void
  onSelect: (company: YtjCompanySuggestion | null) => void
}

/** Taloyhtiön nimikenttä, joka ehdottaa yhtiöitä YTJ:stä; vapaa kirjoitus toimii jos yhtiötä ei löydy */
const HousingCompanySearchField: FC<HousingCompanySearchFieldProps> = ({
  label,
  value,
  error,
  helperText,
  manualEntryLabel,
  onChange,
  onSelect
}) => {
  const [suggestions, setSuggestions] = useState<YtjCompanySuggestion[]>([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [query, setQuery] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    if (query === null) {
      return undefined
    }
    if (query.trim().length < MIN_QUERY_LENGTH) {
      setSuggestions([])
      setOpen(false)
      return undefined
    }
    const timeout = setTimeout(async () => {
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller
      setLoading(true)
      try {
        const response = await fetch(
          `${YTJ_API_URL}?name=${encodeURIComponent(query.trim())}`,
          { signal: controller.signal }
        )
        if (!response.ok) {
          throw new Error(`YTJ responded with ${response.status}`)
        }
        setSuggestions(parseYtjResponse(await response.json()))
        setOpen(true)
      } catch (err) {
        if ((err as any)?.name !== 'AbortError') {
          setSuggestions([])
          setOpen(false)
        }
      } finally {
        setLoading(false)
      }
    }, DEBOUNCE_MS)
    return () => clearTimeout(timeout)
  }, [query])

  const select = (company: YtjCompanySuggestion | null) => {
    if (company) {
      onChange(company.name)
    }
    onSelect(company)
    setQuery(null)
    setSuggestions([])
    setOpen(false)
  }

  return (
    <div style={{ position: 'relative' }}>
      <TextField
        fullWidth
        variant='outlined'
        size='small'
        label={label}
        required
        value={value}
        error={error}
        helperText={helperText}
        autoComplete='off'
        onChange={event => {
          onChange(event.target.value)
          onSelect(null)
          setQuery(event.target.value)
        }}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        onKeyDown={event => event.key === 'Escape' && setOpen(false)}
        InputProps={{
          startAdornment: (
            <InputAdornment position='start'>
              <MagnifyIcon size={20} color='rgba(0,0,0,.54)' />
            </InputAdornment>
          ),
          endAdornment: loading ? (
            <InputAdornment position='end'>
              <CircularProgress size={18} />
            </InputAdornment>
          ) : undefined
        }}
      />
      {open && (
        <Paper
          elevation={8}
          style={{ position: 'absolute', top: 44, left: 0, right: 0, zIndex: 1400, maxHeight: 280, overflowY: 'auto' }}
        >
          {suggestions.map(suggestion => (
            <ListItem
              key={suggestion.businessId}
              button
              onMouseDown={event => {
                event.preventDefault()
                select(suggestion)
              }}
            >
              <ListItemText primary={suggestion.name} secondary={suggestion.businessId} />
            </ListItem>
          ))}
          <ListItem
            button
            divider={false}
            style={{ borderTop: '1px solid #E5E5E5', color: '#4f9b44' }}
            onMouseDown={event => {
              event.preventDefault()
              select(null)
            }}
          >
            <ListItemText primary={manualEntryLabel} />
          </ListItem>
        </Paper>
      )}
    </div>
  )
}

export default HousingCompanySearchField
